<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { PRODUCT_NAME } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import UserDocument from '@/components/layouts/user-document.vue';
import { StorageType, UserDocumentContract, UserDocumentUsage } from '@/types';
import { formatDate } from '@/utils/formatters';
import { checkMoveToAdmin } from '@/utils/user';
import { formatUsageFileSize } from '@/utils/user-document/file';
import { useUserDocumentLimitNotification } from '@/utils/user-document/userDocumentLimitNotification';

type AdminUsageTableItem = {
  id: number;
  order: number;
  name: string;
  storageType: StorageType;
  fileSizeBytes: number;
  uploadSizeBytes: number;
};

const router = useRouter();
const { createSnackbar } = useSnackbar();
const { markAsSeen: markUserDocumentNotificationAsSeen } =
  useUserDocumentLimitNotification();

const usageItems = ref<AdminUsageTableItem[]>([
  {
    id: 3,
    order: 1,
    name: `${PRODUCT_NAME}ストレージ`,
    storageType: 'anews_storage',
    fileSizeBytes: 0,
    uploadSizeBytes: 0,
  },
  {
    id: 1,
    order: 2,
    name: 'BOX',
    storageType: 'box',
    fileSizeBytes: 0,
    uploadSizeBytes: 0,
  },
  {
    id: 2,
    order: 3,
    name: 'SharePoint',
    storageType: 'share_point',
    fileSizeBytes: 0,
    uploadSizeBytes: 0,
  },
]);

const activeContract = ref<UserDocumentContract | undefined>(undefined);

const totalUsage = ref<UserDocumentUsage>({
  total_file_size_bytes: 0,
  total_upload_size_bytes: 0,
  limit_reached: false,
  max_storage_limit_reached: false,
  max_upload_limit_reached: false,
  storage_usages: {},
});

const usageFields = computed<Field<AdminUsageTableItem>[]>(() => [
  {
    name: 'name',
    displayName: 'ストレージ名',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'fileSizeBytes',
    displayName: '使用ストレージ容量',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'uploadSizeBytes',
    displayName: '使用ファイル追加・更新量',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
]);

const displayUsageItems = computed(() =>
  usageItems.value.filter(i => i.fileSizeBytes > 0 || i.uploadSizeBytes > 0),
);

const usageOverview = computed(() => {
  if (!activeContract.value) {
    return {
      activeContractExists: false,
      displayTotalFileSize: '-',
      displayTotalUploadSize: '-',
      maxStorageLimitReached: false,
      maxUploadLimitReached: false,
      showMaxStorageLimitTip: false,
      showMaxUploadLimitTip: false,
      contractDateRange: '-',
    };
  }
  return {
    activeContractExists: true,
    displayTotalFileSize: activeContract.value.max_storage_size_bytes
      ? `${formatUsageFileSize(totalUsage.value.total_file_size_bytes)}/${formatUsageFileSize(activeContract.value.max_storage_size_bytes)}`
      : `${formatUsageFileSize(totalUsage.value.total_file_size_bytes)}`,
    displayTotalUploadSize: activeContract.value.max_upload_size_bytes
      ? `${formatUsageFileSize(totalUsage.value.total_upload_size_bytes)}/${formatUsageFileSize(activeContract.value.max_upload_size_bytes)}`
      : `${formatUsageFileSize(totalUsage.value.total_upload_size_bytes)}`,
    maxStorageLimitReached: totalUsage.value.max_storage_limit_reached,
    maxUploadLimitReached: totalUsage.value.max_upload_limit_reached,
    showMaxStorageLimitTip: !!activeContract.value.max_storage_size_bytes,
    showMaxUploadLimitTip: !!activeContract.value.max_upload_size_bytes,
    contractDateRange: `${formatDate(activeContract.value.start_date)} - ${formatDate(activeContract.value.end_date)}`,
  };
});
const updateAllStorageUsage = async () => {
  totalUsage.value = await api.getTeamUserDocumentUsage();
  usageItems.value.forEach(item => {
    item.fileSizeBytes =
      totalUsage.value.storage_usages[item.storageType]?.file_size_bytes || 0;
    item.uploadSizeBytes =
      totalUsage.value.storage_usages[item.storageType]?.upload_size_bytes || 0;
  });
};

onMounted(async () => {
  // アクセス権限チェック
  await checkMoveToAdmin(router);
  // 社内情報オプションチェック
  const teamInfo = await api.fetchTeamInfo();
  // この画面のアクセス権限
  if (!teamInfo.enable_user_document) {
    createSnackbar({
      message: '権限がありません',
      type: 'error',
    });
    await router.push({ name: 'anewsHome' });
  }
  activeContract.value = await api.getTeamActiveUserDocumentContract();
  await updateAllStorageUsage();
  markUserDocumentNotificationAsSeen();
});
</script>

<template>
  <UserDocument>
    <div class="c-title-bold--m">ストレージ容量</div>
    <div class="container">
      <AdminBaseTable
        :items="displayUsageItems"
        :fields="usageFields"
        :is-sticky-header="true"
        :hide-table="
          !usageOverview.activeContractExists || !displayUsageItems.length
        "
      >
        <template #overview>
          <div class="usage-overview">
            <div class="usage-overview-item">
              <div class="usage-overview-item-title">全体ストレージ容量</div>
              <div
                class="usage-overview-item-value"
                :class="{
                  'limit-reached': usageOverview.maxStorageLimitReached,
                }"
              >
                {{ usageOverview.displayTotalFileSize }}
                <DgrIcon
                  size="small"
                  name="question-circle"
                  v-tooltip.top="{
                    content:
                      'ストレージ容量の上限に達すると新規追加、既存ファイルの更新は反映されません。',
                    theme: 'menu',
                  }"
                  v-if="usageOverview.showMaxStorageLimitTip"
                />
              </div>
            </div>
            <div class="usage-overview-item">
              <div class="usage-overview-item-title">
                全体ファイル追加・更新量
              </div>
              <div
                class="usage-overview-item-value"
                :class="{
                  'limit-reached': usageOverview.maxUploadLimitReached,
                }"
              >
                {{ usageOverview.displayTotalUploadSize }}
                <DgrIcon
                  size="small"
                  name="question-circle"
                  v-tooltip.top="{
                    content:
                      'ファイル追加・更新量の上限に達すると新規追加、既存ファイルの更新が反映されません。',
                    theme: 'menu',
                  }"
                  v-if="usageOverview.showMaxUploadLimitTip"
                />
              </div>
            </div>
            <div class="usage-overview-item">
              <div class="usage-overview-item-title">契約開始日 - 終了日</div>
              <div class="usage-overview-item-value">
                {{ usageOverview.contractDateRange }}
              </div>
            </div>
          </div>
        </template>
        <template #cell-name="{ item }">
          <span class="storage-name" v-text="item.name"></span>
        </template>
        <template #cell-fileSizeBytes="{ item }">
          <span v-text="`${formatUsageFileSize(item.fileSizeBytes)}`"></span>
        </template>
        <template #cell-uploadSizeBytes="{ item }">
          <span v-text="`${formatUsageFileSize(item.uploadSizeBytes)}`"></span>
        </template>
      </AdminBaseTable>
    </div>
  </UserDocument>
</template>

<style scoped lang="scss">
.container {
  margin-top: 16px;
}

.usage-overview {
  display: flex;
  font-size: 14px;
  padding: 16px 0;
  gap: 16px;

  & > div:not(:last-child) {
    border-right: 1px solid $color-gray400;
  }

  &-item {
    padding-right: 16px;

    &-title {
      color: $color-gray800;
      padding-bottom: 8px;
    }

    &-value {
      display: flex;
      gap: 4px;
      align-items: center;
    }
  }
}

.limit-reached {
  color: $color-orange1000;
}
</style>
